// Fichier : Table.tsx
// Ce fichier contient la définition et l'implémentation d'un composant Table utilisant Mantine React Table pour afficher et manipuler des données de Tabs.

import { useMemo } from "react";
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
} from "mantine-react-table";
import { Tab } from "../../types/generale";
import { useNavigate } from "react-router-dom";
import { useDeleteTabMutation } from "@/state/usersApiSlice";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";

type TableProps = {
  tabs: Tab[];
};

const Table: React.FC<TableProps> = ({ tabs }) => {
  const notify = () =>
    toast.success("Tab Deleted Successfully", {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });

  const [deleteTab, { isError }] = useDeleteTabMutation(); // Mutation pour supprimer une Tab
  const navigate = useNavigate(); // Hook de navigation de React Router

  function handleSelectChange(selectedValue: any, row: any) {
    switch (selectedValue) {
      case "edit":
        navigate(`/tabs/${row.original.id}/edit`);
        break;
      case "delete": {
        deleteTab(row.original.id);
        console.log(isError);
        if (!isError) notify();
        break;
      }

      default:
        break;
    }
  }

  // Définition des colonnes de la table
  const columns = useMemo<MRT_ColumnDef<Tab>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        enableColumnFilter: true,
        enableResizing: true,
      },
      {
        accessorKey: "tab_name",
        header: "Tab Name",
        enableColumnFilter: true,
        enableClickToCopy: true,
        enableResizing: true,
      },
      {
        accessorKey: "link",
        header: "Link",
        enableColumnFilter: true,
        enableClickToCopy: true,
        enableResizing: true,
      },
      {
        accessorKey: "link_type",
        header: "Link Type",
        enableColumnFilter: true,
        enableClickToCopy: true,
        enableResizing: true,
      },
      // {
      //   accessorKey: "actions",
      //   header: "Actions",
      //   enableSorting: false,
      //   enableColumnFilter: false,
      //   enableResizing: true,
      //   Cell: ({ row }) => (
      //     <div className="flex gap-1">
      //       {/* Bouton d'édition */}
      //       <button
      //         onClick={() => navigate(`/tabs/${row.original.id}/edit`)}
      //         className="hover:text-[#5f2a6d] hover:cursor-pointer w-28 h-12 bg-green-500 text-white rounded-lg"
      //       >
      //         Edit
      //       </button>
      //       {/* Bouton de suppression */}
      //       <button
      //         onClick={() => {
      //           deleteTab(row.original.id); // Appel à la fonction de suppression
      //           notify(); // Notification de suppression réussie
      //         }}
      //         className="hover:text-[#5f2a6d] hover:cursor-pointer w-28 h-12 bg-red-500 text-white rounded-lg"
      //       >
      //         Delete
      //       </button>
      //     </div>
      //   ),
      // },
      {
        accessorKey: "actions",
        header: "Actions",
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
        enableSorting: false, // Désactivation du tri pour cette colonne
        Cell: (
          { row } // Composant Cell personnalisé pour afficher le menu déroulant des actions , il contient un select avec des options qui seront trackés par le switch au dessus.
        ) => (
          <div className="relative flex items-center">
            <FontAwesomeIcon
              icon={faEllipsisV}
              className="absolute left-11 pointer-events-none"
              size={"lg"}
            />
            <select
              className="pr-6 appearance-none border-none text-center font-bold  py-2 rounded leading-tight outline-none focus:border-blue-500"
              onChange={(e) => handleSelectChange(e.target.value, row)}
            >
              <option value=""></option>
              <option value="edit">EDIT</option>
              <option value="delete">DELETE</option>
            </select>
          </div>
        ),
      },
    ],
    []
  );

  // Initialisation de la table avec les colonnes et les données
  const table = useMantineReactTable({
    columns,
    data: tabs,
    enableColumnOrdering: false,
    enableColumnActions: false,
    enableColumnDragging: false,
    enableHiding: false,
    enableSorting: true,
    enableColumnPinning: false,
    defaultColumn: {
      minSize: 1,
      maxSize: 3,
      size: 2,
    },
    mantineFilterTextInputProps: {
      size: "xs",
    },

    enableFacetedValues: true,
    enableRowActions: false,
    enableGrouping: true,
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: true,
      density: "xs",
    },
    mantineTableBodyCellProps: {
      sx: {
        textAlign: "center",
        fontSize: "11px !important",
      },
    },
    mantineTableHeadCellProps: {
      sx: {
        fontSize: "11px !important",
        fontWeight: "bold",
      },
    },
    enablePagination: false,
    enableRowSelection: false,
    enableGlobalFilter: false,
  });

  return <MantineReactTable table={table} />; // Rendu de la table avec les configurations définies
};

export default Table;
